// The CTV creative builder of the creative builder: one 16:9 video and a
// click-through per creative, each attached to every TTD ad group. Split out of
// creative.tsx, which still exports it.

import { useEffect, useId, useRef, useState } from 'react';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useCreatives } from '@/api/hooks';
import type { Creative } from '@/api/types';
import { isSupportedCreativeMime } from '@/api/creative-upload';
import { RequiredField } from '@/components/required-field';
import { libraryToAssets } from '@/lib/creative-library-assets';
import { LibraryPicker } from './library-picker';
import { PinnedVersionBadge } from '@/components/pinned-version-badge';
import type { PinnedVersionBadgeProps } from '@/components/pinned-version-badge';
import { CreativeSlotCard } from '@/components/creative-slot-card';
import { posterInitials } from '@/lib/creative-slot-view';
import {
  assetSizeLabel,
  ctvAssetFieldKey,
  ctvDestinationFieldKey,
  type CreativeBuilderProduction,
  type CreativeSlot,
  type CtvSlotData,
} from '@/lib/creative-required-issues';
import type { ChannelConfig } from './creative-config';
import { defaultAdName, type CreativeRequiredFeedback } from './creative-meta-editor';

/** The badge props a published campaign's slot adds: who rolls it out, and how. */
export type CreativeRolloutBadgeProps = Partial<Pick<
  PinnedVersionBadgeProps,
  'campaignName' | 'provider' | 'rollout' | 'rolloutPending' | 'onRollout'
>>;

export interface CtvCreativeBuilderProps {
  /** The CTV channel config: its video spec. */
  cfg: { ctv: NonNullable<ChannelConfig['ctv']> };
  production: CreativeBuilderProduction | null | undefined;
  /** The slots. Without them the builder keeps its own. */
  creatives?: CreativeSlot[];
  setCreatives?: Dispatch<SetStateAction<CreativeSlot[]>>;
  /** Each slot's destination and video, by slot id. Without them the builder keeps its own. */
  data?: Record<string, CtvSlotData>;
  setData?: Dispatch<SetStateAction<Record<string, CtvSlotData>>>;
  singleMode?: boolean;
  availableCount?: number;
  requiredFeedback?: CreativeRequiredFeedback;
  locked?: boolean;
  rolloutFor?: (position: number) => CreativeRolloutBadgeProps;
}

/** One uploaded video's probe: its pixel size, its length in seconds, and its object URL. */
interface VideoProbe {
  w: number;
  h: number;
  duration: number;
  url: string;
}

export function CtvCreativeBuilder({ cfg, production, creatives: creativesProp, setCreatives: setCreativesProp, data: dataProp, setData: setDataProp, singleMode, availableCount = 0, requiredFeedback, locked = false, rolloutFor = () => ({}) }: CtvCreativeBuilderProps) {
  const adNameId = useId();
  const { data: libraryCreatives = [] } = useCreatives();
  const [creativesInner, setCreativesInner] = useState<CreativeSlot[]>([{ id: 'ctv-1', label: 'Creative 1' }]);
  const [dataInner, setDataInner] = useState<Record<string, CtvSlotData>>({ 'ctv-1': { destUrl: '', asset: null } });
  const creatives = creativesProp || creativesInner;
  const setCreatives = setCreativesProp || setCreativesInner;
  const data = dataProp || dataInner;
  const setData = setDataProp || setDataInner;
  const [activeId, setActiveId] = useState(creatives[0]?.id || 'ctv-1');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const pendingForRef = useRef<string | null>(null);

  const update = (id: string, patch: Partial<CtvSlotData>) => setData(d => ({ ...d, [id]: { ...(d[id] || {destUrl:'',asset:null}), ...patch } }));
  const cur = data[activeId] || { destUrl: '', asset: null };
  const destinationRequiredKey = ctvDestinationFieldKey(activeId);
  const assetRequiredKey = ctvAssetFieldKey(activeId);

  useEffect(() => {
    const firstInvalid = creatives.find((creative) =>
      requiredFeedback?.messages?.[ctvDestinationFieldKey(creative.id)]
      || requiredFeedback?.messages?.[ctvAssetFieldKey(creative.id)]);
    if (firstInvalid && firstInvalid.id !== activeId) {
      setActiveId(firstInvalid.id);
      setErrMsg(null);
    }
  }, [requiredFeedback?.attempt]);

  const addCreative = () => {
    const id = `ctv-${Date.now()}`;
    setCreatives([...creatives, { id, label: `Creative ${creatives.length + 1}` }]);
    setData({ ...data, [id]: { destUrl: '', asset: null } });
    setActiveId(id);
    setErrMsg(null);
  };
  const removeCreative = (id: string) => {
    if (creatives.length <= 1) return;
    const next = creatives.filter(c => c.id !== id);
    setCreatives(next);
    setData(d => {
      const url = d[id]?.asset?.url;
      if (url) URL.revokeObjectURL(url);
      const { [id]: _, ...rest } = d;
      return rest;
    });
    const first = next[0];
    if (activeId === id && first) setActiveId(first.id);
  };

  const probeVideo = (file: File) => new Promise<VideoProbe>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => resolve({ w: v.videoWidth, h: v.videoHeight, duration: v.duration, url });
    v.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read video metadata. File may be corrupt.')); };
    v.src = url;
  });

  const c = cfg.ctv;
  const openPicker = () => {
    setErrMsg(null);
    pendingForRef.current = activeId;
    fileInput.current?.click();
  };

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const targetId = pendingForRef.current || activeId;
    setErrMsg(null);
    if (!file.type.startsWith('video') || !isSupportedCreativeMime(file.type)) {
      setErrMsg(`Unsupported file type · ${file.type || 'unknown'}. CTV accepts MP4 or MOV video files only.`);
      return;
    }
    if (file.size > c.maxBytes) {
      const fm = (b: number) => b >= 1024*1024 ? (b/1024/1024).toFixed(1) + ' MB' : (b/1024).toFixed(0) + ' KB';
      setErrMsg(`File too large · ${fm(file.size)} exceeds the ${fm(c.maxBytes)} CTV limit.`);
      return;
    }
    setValidating(true);
    try {
      const probe = await probeVideo(file);
      const ratio = probe.w / probe.h;
      const fail = (msg: string) => { URL.revokeObjectURL(probe.url); setErrMsg(msg); };
      if (Math.abs(ratio - c.ratio) > c.ratioTol) {
        fail(`Wrong aspect ratio · got ${probe.w}×${probe.h} (${ratio.toFixed(3)}), need 16:9 (1.778).`);
        return;
      }
      if (probe.w < c.minW || probe.h < c.minH) {
        fail(`Resolution too low · got ${probe.w}×${probe.h}, minimum is ${c.minW}×${c.minH}.`);
        return;
      }
      const matchedDur = c.durations.find(d => Math.abs(probe.duration - d) <= c.durTol);
      if (!matchedDur) {
        fail(`Wrong duration · got ${probe.duration.toFixed(1)}s. CTV accepts ${c.durations.map(d=>d+'s').join(' or ')} (±${c.durTol}s).`);
        return;
      }
      // replace existing asset for the target creative
      setData(prev => {
        const t = prev[targetId];
        if (t?.asset?.url) URL.revokeObjectURL(t.asset.url);
        return {
          ...prev,
          [targetId]: {
            ...(t || {destUrl:''}),
            asset: {
              file,
              type: 'video',
              name: file.name,
              url: probe.url,
              duration: matchedDur,
              durationMs: Math.round(probe.duration * 1000),
              width: probe.w,
              height: probe.h,
              w: probe.w,
              h: probe.h,
            },
          },
        };
      });
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setValidating(false);
    }
  };

  const removeAsset = () => {
    if (cur.asset?.url) URL.revokeObjectURL(cur.asset.url);
    update(activeId, { asset: null });
  };

  // The slot being edited, and the library version it is pinned to.
  const activeSlot = creatives.find(cc=>cc.id===activeId);
  const activePin = activeSlot?.fromLibrary;

  return (
    <>
      {!singleMode && (
        <>
          <div className="creative-slot-intro">
            <p>Each creative is attached to every ad group on The Trade Desk. Upload a 15s or 30s 16:9 video, or pick an approved creative from the library; the one you are editing is outlined.</p>
            <div style={{display:'flex',gap:8,flexShrink:0}}>
              {/* Offered only with something to pick: it opened an empty picker (F5 UX P8). */}
              <button type="button" className="btn" disabled={availableCount === 0} onClick={()=>setPickerOpen(true)} title="Pick existing CTV creatives from the library">
                {availableCount === 0 ? 'Library · no CTV creatives yet' : `Pick from library · ${availableCount} available`}
              </button>
              <button type="button" className="btn btn-primary" onClick={addCreative} title="Add another video creative">
                New creative
              </button>
            </div>
          </div>
          <div className="creative-slot-list creative-slot-list--draft" style={{marginBottom:4}}>
            {creatives.map((c) => {
              const name = c.adName || defaultAdName(production, c.label, 'ctv');
              const asset = data[c.id]?.asset;
              const destination = data[c.id]?.destUrl;
              return (
                <div key={c.id} className={activeId === c.id ? 'is-active' : undefined}>
                <CreativeSlotCard
                  ariaLabel={`Creative slot: ${name}`}
                  name={name}
                  poster={asset?.url ? { url: asset.url, kind: 'video', ratio: '16:9' } : null}
                  initials={posterInitials(name)}
                  isVideo
                  mediaLabel="Video"
                  formatLabel={asset ? `${asset.duration ? `${Math.round(asset.duration)}s · ` : ''}16:9` : 'No video yet'}
                  versionLabel={c.fromLibrary ? `From library · v${c.fromLibrary.revision}` : 'New · draft'}
                  provider="ttd"
                  tone="quiet"
                  aside={
                    <>
                      <span className="creative-slot-eyebrow">Destination</span>
                      <dl className="creative-slot-lines">
                        <div className="creative-slot-line">
                          <dt>Click-through</dt>
                          <dd style={destination ? undefined : {color:'var(--bad)'}}>{destination || 'Required · not set'}</dd>
                        </div>
                        <div className="creative-slot-line">
                          <dt>Video</dt>
                          <dd style={asset ? undefined : {color:'var(--bad)'}}>{asset ? assetSizeLabel(asset) : 'Required · not uploaded'}</dd>
                        </div>
                      </dl>
                    </>
                  }
                  actions={
                    <>
                      <button type="button" className={`btn btn-sm${activeId === c.id ? ' btn-primary' : ''}`} aria-label={`Edit creative ${name}`} aria-pressed={activeId === c.id} onClick={()=>{ setActiveId(c.id); setErrMsg(null); }}>
                        {activeId === c.id ? 'Editing' : 'Edit'}
                      </button>
                      {creatives.length > 1 && (
                        <button type="button" className="btn btn-sm btn-ghost" aria-label={`Remove creative ${name}`} onClick={()=>removeCreative(c.id)}>
                          Remove
                        </button>
                      )}
                    </>
                  }
                />
                </div>
              );
            })}
          </div>
          <div className="creative-slot-editing">
            <h4>Editing · {creatives.find((c) => c.id === activeId)?.adName || defaultAdName(production, creatives.find((c) => c.id === activeId)?.label || 'Creative', 'ctv')}</h4>
          </div>
        </>
      )}

      <LibraryPicker open={pickerOpen} channel="ctv" currentShow={production} multi onClose={()=>setPickerOpen(false)} onPick={(list: Creative[])=>{
        if (!list || !list.length) return;
        const newCreatives = [...creatives];
        const newData = { ...data };
        const nowTs = Date.now();

        // If active CTV creative is empty (no video asset), replace it with first pick.
        const activeIdx = newCreatives.findIndex(c => c.id === activeId);
        const active = newCreatives[activeIdx];
        const activeCur = active ? data[active.id] : null;
        const activeIsEmpty = !!active && !activeCur?.asset;

        let startIdx = 0;
        let firstInsertedId: string | null = null;
        const first = list[0];
        if (activeIsEmpty && first) {
          const cr = first;
          const oldId = active.id;
          const id = `ctv-lib-${cr.id}-${nowTs}-0`;
          newCreatives[activeIdx] = { id, label: active.label, adName: cr.name, fromLibrary: { id: cr.id, name: cr.name, revision: cr.approved_revision ?? cr.revision } };
          delete newData[oldId];
          newData[id] = libraryToAssets(cr, production, 'ctv');
          firstInsertedId = id;
          startIdx = 1;
        }

        for (let i = startIdx; i < list.length; i++) {
          const cr = list[i];
          if (!cr) continue;
          const id = `ctv-lib-${cr.id}-${nowTs}-${i}`;
          newCreatives.push({ id, label: `Creative ${newCreatives.length + 1}`, adName: cr.name, fromLibrary: { id: cr.id, name: cr.name, revision: cr.approved_revision ?? cr.revision } });
          newData[id] = libraryToAssets(cr, production, 'ctv');
          if (!firstInsertedId) firstInsertedId = id;
        }

        setCreatives(newCreatives);
        setData(newData);
        setActiveId(firstInsertedId || newCreatives[newCreatives.length - 1]?.id || activeId);
      }}/>

      <input ref={fileInput} type="file" accept="video/*" style={{display:'none'}} onChange={handleFile}/>

      <div className="flex between items-end mb-2" style={{gap:10}}>
        <div>
          <div className="field-lbl" style={{margin:0}}>Video asset · 1 per creative</div>
          <div className="muted" style={{fontSize:11,marginTop:2}}>16:9 · 1920×1080 min · MP4 H.264 · {c.durations.map(d=>d+'s').join(' or ')} · {(c.maxBytes/1024/1024).toFixed(0)}MB max</div>
        </div>
      </div>

      <div className="facet" style={{margin:'0 0 12px', padding:'10px 12px'}}>
        <div className="facet-hd" style={{marginBottom:6}}>
          <h5 style={{fontSize:10}}><label htmlFor={adNameId}>Ad Name</label></h5>
          <span className="muted" style={{fontSize:10}}>Internal name — not shown to viewers{activePin ? <> · <span style={{color:'var(--accent)'}}>from library: {activePin.name}</span></> : null}</span>
          {activePin && (
            <PinnedVersionBadge
              creative={libraryCreatives.find(lc => lc.id === activePin.id)}
              pinnedRevision={activePin.revision}
              campaignLive={!!locked}
              {...rolloutFor(creatives.findIndex(cc=>cc.id===activeId))}
            />
          )}
        </div>
        <input
          id={adNameId}
          type="text"
          value={activeSlot?.adName ?? defaultAdName(production, activeSlot?.label, 'ctv')}
          onChange={e=>setCreatives(creatives.map(cc => cc.id===activeId ? {...cc, adName: e.target.value} : cc))}
          className="input"
          style={{width:'100%', fontSize:12}}
        />
      </div>

      <RequiredField
        fieldKey={destinationRequiredKey}
        message={requiredFeedback?.messages?.[destinationRequiredKey]}
        attempt={requiredFeedback?.attempt ?? 0}
        surface
        className="facet"
        style={{margin:'0 0 12px', padding:'10px 12px'}}
      >
        <div className="facet-hd" style={{marginBottom:6}}>
          <h5 style={{fontSize:10}}>Destination URL <span style={{color:'var(--live)'}}>*</span></h5>
          <span className="muted" style={{fontSize:10}}>Required · click-through landing page for this creative</span>
        </div>
        <input
          data-validation-focus
          type="url"
          value={cur.destUrl}
          onChange={e=>update(activeId, { destUrl: e.target.value })}
          placeholder="https://www.shubert.nyc/production/..."
          className="input"
          style={{width:'100%', fontFamily:'var(--font-mono)', fontSize:12}}
        />
      </RequiredField>

      <RequiredField
        fieldKey={assetRequiredKey}
        message={requiredFeedback?.messages?.[assetRequiredKey]}
        attempt={requiredFeedback?.attempt ?? 0}
        surface
        style={{maxWidth:380}}
      >
        {cur.asset ? (
          <div className="creative-asset" style={{aspectRatio:'16/9', position:'relative', overflow:'hidden'}}>
            <video src={cur.asset.url} muted loop autoPlay playsInline style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
            <div style={{position:'absolute',top:0,left:0,right:0,height:50,background:'linear-gradient(to bottom, rgba(0,0,0,.6), transparent)',pointerEvents:'none'}}/>
            <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'16px 10px 8px',background:'linear-gradient(to top, rgba(0,0,0,.75), transparent)',pointerEvents:'none'}}>
              <div style={{fontFamily:'var(--font-mono)',fontSize:9.5,color:'rgba(255,255,255,.85)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{cur.asset.name}</div>
            </div>
            <div style={{position:'absolute',top:6,left:8,fontFamily:'var(--font-display)',fontWeight:800,fontSize:14,color:'#fff',textShadow:'0 1px 2px rgba(0,0,0,.5)',letterSpacing:'.02em'}}>{cur.asset.duration}s</div>
            <button className="asset-x" onClick={removeAsset} title="Remove asset">×</button>
            <button className="btn btn-sm" style={{position:'absolute',bottom:8,right:8,fontSize:10}} onClick={openPicker}>Replace</button>
          </div>
        ) : (
          <button data-validation-focus className="ctv-add" style={{aspectRatio:'16/9'}} onClick={openPicker} disabled={validating}>
            <div style={{fontSize:28,lineHeight:1,marginBottom:4}}>+</div>
            <div style={{fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.1em',textTransform:'uppercase'}}>{validating ? 'Validating…' : 'Upload Video'}</div>
            <div className="muted" style={{fontSize:10,marginTop:4}}>15s or 30s · 1920×1080</div>
          </button>
        )}
      </RequiredField>

      {errMsg && (
        <div className="asset-err" style={{marginTop:10}}>
          <span style={{flexShrink:0}}>⚠</span>
          <span style={{flex:1}}>{errMsg}</span>
          <button className="asset-err-x" onClick={()=>setErrMsg(null)} title="Dismiss">×</button>
        </div>
      )}
    </>
  );
}
